import React, { useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form/dist/index.ie11';
import {
  Button,
  Container,
  Heading,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { useHistory } from 'react-router-dom';
import { ConfirmationStepCard } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStepCard/ConfirmationStepCard';
import { SignupStepFooter } from 'src/components/selfService/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { SignupStepFooterActions } from 'src/components/selfService/features/Signup/components/SignupStepFooterActions/SignupStepFooterActions';
import {
  getSignupSteps,
  SignupStep,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { ISignupStepProps } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTrackerStep/SignupProgressTrackerStep';
import { sdk } from 'src/utilities/sdk';
import { SignupErrorDialog } from 'src/components/selfService/features/Signup/components/SignupErrorDialog/SignupErrorDialog';
import { SignupErrorDialogVariant } from 'src/components/selfService/features/Signup/components/SignupErrorDialog/signupErrorDialog.constants';
import { AnalyticsEventLabel } from 'src/types/Analytics';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { TermsAndConditionsControl } from 'src/components/common/TermsAndConditionsControl/TermsAndConditionsControl';
import { useScreenSizes } from 'src/utilities/useScreenSizes';

export function ConfirmationStep({
  updateMobileStepSize,
}: ISignupStepProps): JSX.Element {
  const [signupSteps] = useState(
    getSignupSteps(true).filter((step) => step.id !== SignupStep.SUMMARY)
  );
  const {
    corpId,
    isWelcomeBackModalPresent,
    goToPreviousStep,
    sendBackButtonAnalytics,
  } = useSignupStatus();
  const [showSubmitErrorDialog, setShowSubmitErrorDialog] = useState(false);
  const { status: submissionStatus, startAsyncTask } = useAsyncTask();
  const isSubmitting = submissionStatus === AsyncTaskStatus.IN_PROGRESS;
  const isSubmissionCompleted = submissionStatus === AsyncTaskStatus.COMPLETE;
  const isSubmissionError = submissionStatus === AsyncTaskStatus.ERROR;
  const { isPhone } = useScreenSizes();

  const useFormProps = useForm({
    mode: 'all',
    defaultValues: {
      termsAndConditionsChecked: false,
    },
  });

  const {
    formState: { isValid },
    handleSubmit,
  } = useFormProps;

  const { push } = useHistory();

  const { sendTealiumPageView, sendTealiumEvent } = useAnalytics();

  useEffect(() => {
    if (!isWelcomeBackModalPresent) {
      // send analytics
      sendTealiumPageView({
        pageGroup: 'corporate self service - sign up flow',
        pageName: 'review',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWelcomeBackModalPresent]);

  useEffect(() => {
    if (isSubmissionCompleted) {
      const baseManagementUrl = `/corporate/management/${corpId}`;

      push(`${baseManagementUrl}/home?firstTime=true`);

      sendTealiumEvent({
        eventcategory: 'self-service sign up_review',
        eventaction: 'submit_cta',
        eventlabel: AnalyticsEventLabel.SUCCESS,
      });
    }

    if (isSubmissionError) {
      sendTealiumEvent({
        eventcategory: 'self-service sign up_review',
        eventaction: 'submit_cta',
        eventlabel: AnalyticsEventLabel.ERROR,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmissionCompleted, isSubmissionError]);

  async function submitCorp() {
    try {
      await startAsyncTask(() => {
        return sdk.http.post(`/corp-admin/corp/self/${corpId}/signup`, {});
      });
    } catch (error) {
      setShowSubmitErrorDialog(true);
    }
  }

  const onDataLoaded = useCallback((): void => {
    if (updateMobileStepSize) {
      updateMobileStepSize();
    }
  }, [updateMobileStepSize]);

  return (
    <>
      <FormProvider {...useFormProps}>
        <form onSubmit={handleSubmit(submitCorp)}>
          <Container variant="sm">
            <Stack y="sm">
              <Heading variant="h1">Review and submit</Heading>
            </Stack>
            {signupSteps.map(({ id }, index) => {
              const isLastStep = index === signupSteps.length - 1;

              return (
                <Stack key={id} y={isLastStep ? 'md' : 'sm'}>
                  <ConfirmationStepCard
                    stepId={id}
                    onDataLoaded={onDataLoaded}
                  />
                </Stack>
              );
            })}
            <TermsAndConditionsControl
              name="termsAndConditionsChecked"
              id="termsAndConditionsChecked"
              rules={{ required: true }}
            />
          </Container>
          <SignupStepFooter>
            <SignupStepFooterActions
              isUpdating={false}
              back={
                <Button
                  variant="secondary"
                  buttonSize="medium"
                  block={isPhone}
                  onClick={() => {
                    sendBackButtonAnalytics();
                    goToPreviousStep();
                  }}
                >
                  Back
                </Button>
              }
              next={
                <Button
                  variant="primary"
                  flavor="success"
                  type="submit"
                  block={isPhone}
                  buttonSize="medium"
                  disabled={isSubmitting || isSubmissionCompleted || !isValid}
                  isLoading={isSubmitting}
                  onClick={handleSubmit(submitCorp)}
                >
                  Submit
                </Button>
              }
            />
          </SignupStepFooter>
        </form>
      </FormProvider>
      {showSubmitErrorDialog && (
        <SignupErrorDialog
          variant={SignupErrorDialogVariant.COMPLETION_UNSUCCESSFUL}
          onDismiss={() => setShowSubmitErrorDialog(false)}
        />
      )}
    </>
  );
}
